import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { formatGBP, formatDate } from "@/lib/utils-cart";
import { GatewayBadge, PaymentStatusBadge } from "@/components/Badges";
import { CheckCircle2 } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [id]);

  if (!order) return <PublicLayout><div className="p-20 text-center">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center">
          <div className="inline-flex w-16 h-16 items-center justify-center bg-accent-green/10 rounded-sm">
            <CheckCircle2 size={32} className="text-accent-green" />
          </div>
          <h1 className="mt-6 font-display text-4xl font-black tracking-tighter text-primary">Order confirmed.</h1>
          <p className="mt-2 text-muted-foreground">A receipt has been sent to {order.buyer_email}.</p>
          <div className="mt-4 inline-flex items-center gap-2 font-mono text-sm text-muted-foreground">
            <span>{order.order_number}</span> · <PaymentStatusBadge status={order.payment_status} /> · <GatewayBadge gateway={order.gateway} />
          </div>
        </div>

        <div className="mt-10 v-card">
          <h2 className="font-display text-lg font-semibold text-primary">Order details</h2>
          <div className="mt-4 divide-y divide-border">
            {order.items.map((it, i) => (
              <div key={i} className="py-3 flex items-center gap-3">
                <img src={it.image} alt={it.title} className="w-14 h-14 object-cover bg-muted rounded-sm" />
                <div className="flex-1">
                  <div className="font-medium text-primary">{it.title}</div>
                  <div className="text-xs font-mono text-muted-foreground">× {it.quantity} @ {formatGBP(it.unit_price)}</div>
                </div>
                <div className="font-mono">{formatGBP(it.line_total)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
            <Row label="Subtotal" value={formatGBP(order.subtotal)} />
            <Row label="Includes VAT" value={formatGBP(order.tax)} />
            <Row label="Shipping" value={order.shipping ? formatGBP(order.shipping) : "Free"} />
            <div className="pt-3 mt-1 border-t border-border flex items-baseline justify-between">
              <span className="font-bold text-primary">Total ({order.currency})</span>
              <span className="font-display font-black text-2xl">{formatGBP(order.total)}</span>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground font-mono">
            Placed {formatDate(order.created_at)} · Ships from supplier · Tracking emailed when label is created.
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-3">
          <Link to="/marketplace" className="v-btn-outline" data-testid="success-continue">Continue shopping</Link>
          <Link to="/app/orders" className="v-btn-primary" data-testid="success-view-orders">View my orders</Link>
        </div>
      </section>
    </PublicLayout>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className="font-mono">{value}</span></div>;
}
